import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack, Delete, Edit, Share } from '@mui/icons-material';
import { useDeletePlayer, usePlayer, usePlayerStats } from '@/hooks/useApi';
import { useAdminMode } from '@/hooks/useManage';
import { ErrorState, LoadingState } from '@/components';
import { KebabMenu, shareLink } from '@/components/manage';
import { useNotification } from '@/contexts/NotificationContext';

const STAT_ROWS: Array<{ key: 'matchesPlayed' | 'goals' | 'assists' | 'points' | 'saves' | 'rebounds' | 'blocks'; label: string; main?: boolean }> = [
  { key: 'matchesPlayed', label: 'Матчи', main: true },
  { key: 'goals', label: 'Голы', main: true },
  { key: 'assists', label: 'Передачи', main: true },
  { key: 'points', label: 'Очки' },
  { key: 'saves', label: 'Сейвы' },
  { key: 'rebounds', label: 'Подборы' },
  { key: 'blocks', label: 'Блоки' },
];

export const PlayerDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAdminMode();
  const { showSuccess } = useNotification();
  const [detailed, setDetailed] = useState(false);
  const { data: player, isLoading, error, refetch } = usePlayer(id);
  const { data: stats } = usePlayerStats(id, { scope: 'career' });
  const deletePlayer = useDeletePlayer();

  if (isLoading) return <LoadingState fullscreen message="Загрузка игрока..." />;
  if (error || !player) {
    return (
      <Box sx={{ p: 2 }}>
        <IconButton aria-label="Назад" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          <ArrowBack />
        </IconButton>
        <ErrorState message="Ошибка загрузки игрока" onRetry={refetch} />
      </Box>
    );
  }

  const name = [player.firstName, player.lastName].filter(Boolean).join(' ');
  const rows = STAT_ROWS.filter((r) => detailed || r.main);

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 2 }}>
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, fontSize: '1.4rem' }}>
          {`${player.firstName.charAt(0)}${player.lastName?.charAt(0) ?? ''}`.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h2" component="h1">
            {name}
          </Typography>
          {!player.active && <Chip label="Неактивен" size="small" sx={{ mt: 0.5 }} />}
        </Box>
        <KebabMenu
          items={[
            {
              id: 'share',
              label: 'Поделиться',
              icon: <Share fontSize="small" />,
              onClick: async () => {
                try {
                  if ((await shareLink(name)) === 'copied') showSuccess('Ссылка скопирована');
                } catch {
                  /* отмена системного меню */
                }
              },
            },
            {
              id: 'edit',
              label: 'Изменить',
              icon: <Edit fontSize="small" />,
              hidden: !can(player.permissions?.update),
              onClick: () => navigate(`/players/${id}/edit`),
            },
            {
              id: 'delete',
              label: 'Удалить',
              icon: <Delete fontSize="small" />,
              danger: true,
              hidden: !can(player.permissions?.delete),
              confirm: { title: 'Удалить игрока?' },
              onClick: async () => {
                await deletePlayer.mutateAsync(id);
                navigate('/players', { replace: true });
              },
            },
          ]}
        />
      </Paper>

      <Typography variant="h4" sx={{ mt: 2, mb: 1, px: 1 }}>
        Статистика за карьеру
      </Typography>
      {stats ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key} sx={{ height: 44 }}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align="right">
                    <strong>{stats[r.key] ?? 0}</strong>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
            <Button size="small" onClick={() => setDetailed((v) => !v)} sx={{ minHeight: 40 }}>
              {detailed ? 'Кратко' : 'Подробно'}
            </Button>
          </Box>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">Нет данных о статистике</Typography>
        </Paper>
      )}
    </Box>
  );
};
